import { useState, useRef, useCallback, useEffect } from 'react'

const getSR = () =>
  typeof window !== 'undefined'
    ? window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null
    : null

// SpeechRecognition error codes → what the person can do about it. null = stay quiet.
const ERRORS = {
  'no-speech': 'Ich habe nichts gehört. Tippe aufs Mikrofon und sprich gleich los.',
  'audio-capture': 'Kein Mikrofon gefunden.',
  'not-allowed': 'Das Mikrofon ist nicht erlaubt. Erlaube es in den Browser-Einstellungen für diese Seite.',
  'service-not-allowed': 'Das Mikrofon ist nicht erlaubt. Erlaube es in den Browser-Einstellungen für diese Seite.',
  network: 'Die Spracherkennung braucht eine Internetverbindung.',
  'language-not-supported': 'Dieser Browser erkennt kein Deutsch.',
  aborted: null,
}

/**
 * Web Speech API dictation, German by default.
 *
 * - `interim` is the live transcript while the person talks; `hearing` is true while
 *   the browser detects speech (drives the listening animation).
 * - The final transcript goes to `onResult` once recognition ends. `stop()` sends what
 *   was heard so far (tap = "done"); `cancel()` throws it away.
 *
 * @param {{ onResult: (t: string) => void, onError?: (msg: string) => void, lang?: string }} opts
 */
export function useVoiceCommand({ onResult, onError, lang = 'de-DE' } = {}) {
  const [listening, setListening] = useState(false)
  const [hearing, setHearing] = useState(false)
  const [interim, setInterim] = useState('')
  const recRef = useRef(null)
  const cb = useRef({ onResult, onError })
  useEffect(() => { cb.current = { onResult, onError } })
  const supported = !!getSR()

  const start = useCallback(() => {
    const SR = getSR()
    if (!SR) return cb.current.onError?.('Dieser Browser kann keine Sprache erkennen.')
    if (recRef.current) return

    const rec = new SR()
    rec.lang = lang
    rec.continuous = false
    rec.interimResults = true
    rec.maxAlternatives = 1
    const session = { final: '', heard: '', cancelled: false }

    rec.onresult = (e) => {
      let fin = '', mid = ''
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0]?.transcript ?? ''
        if (e.results[i].isFinal) fin += t
        else mid += t
      }
      session.final = fin.trim()
      session.heard = `${fin}${mid}`.trim()
      setInterim(session.heard)
    }
    rec.onspeechstart = () => setHearing(true)
    rec.onspeechend = () => setHearing(false)
    rec.onerror = (e) => {
      // A "no-speech" after something was heard is just the end of the sentence.
      if (e.error === 'no-speech' && session.heard) return
      const msg = e.error in ERRORS ? ERRORS[e.error] : `Spracherkennung: ${e.error}`
      if (msg) cb.current.onError?.(msg)
      session.cancelled = true
    }
    rec.onend = () => {
      recRef.current = null
      setListening(false)
      setHearing(false)
      setInterim('')
      const text = session.final || session.heard
      if (!session.cancelled && text) cb.current.onResult?.(text)
    }
    rec.session = session

    try {
      rec.start()
    } catch {
      return   // already running elsewhere, or no user gesture (auto-listen after a spoken question)
    }
    recRef.current = rec
    setListening(true)
    navigator.vibrate?.(12)
  }, [lang])

  // Ends recognition and sends what was heard so far.
  const stop = useCallback(() => { recRef.current?.stop() }, [])

  // Ends recognition without sending anything.
  const cancel = useCallback(() => {
    const rec = recRef.current
    if (!rec) return
    rec.session.cancelled = true
    rec.abort()
  }, [])

  useEffect(() => () => recRef.current?.abort(), [])

  return { listening, hearing, interim, start, stop, cancel, supported }
}
